import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, RotateCcw, Save } from 'lucide-react';
import { AxiosError } from 'axios';
import { api } from '@/api/client';
import {
    useCreateTxPlan,
    useDeleteTxPlan,
    useSignBcba,
    useSignParent,
    useTransitionTxPlan,
    useTxPlan,
    useUpdateTxPlan,
    useSendPlanToPortal,
    useRecallPlanFromPortal,
    exportTxPlanDocx,
    defaultPlanMembership,
    type TxPlan,
    type TxPlanStatus,
} from '@/api/txPlans';
import { useTemplatesForExport } from '@/api/tpTemplates';
import { useClient } from '@/api/clients';
import { useClientBehaviors } from '@/api/clientBehaviors';
import { useClientPrograms } from '@/api/clientPrograms';
import { useClientProgramTargets } from '@/api/clientProgramTargets';
import { useUserDirectory } from '@/api/users';
import { useTxPlanCustomFieldDefs } from '@/api/customFields';
import { useActiveParentRelationships } from '@/api/parentRelationships';
import {
    CustomFieldsForm,
    initCustomFieldsForm,
    validateCustomFieldsForm,
    type CustomFieldFormState,
} from '@/features/clients/CustomFieldsForm';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { SelectField } from '@/features/clients/SelectField';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { SignatureCanvas, type SignatureCanvasHandle } from '@/features/sessions/SignatureCanvas';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Treatment Plan builder + detail.
 *
 * One page covers three modes:
 *   - /treatment-plans/plans/new?client=<id>  → draft create form
 *   - /treatment-plans/plans/:id (Draft)      → editable detail + signatures
 *   - /treatment-plans/plans/:id (!Draft)     → read-only detail + transitions
 *
 * Draft is the only editable state. Signed/Active/Archived show a read-only
 * summary plus available transition actions. Scratching a Signed plan back
 * to Draft nulls every signature column server-side in one atomic write.
 */

function formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
}

export function PlanBuilderPage() {
    const { id } = useParams<{ id?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const canEdit = useAuthStore((s) => s.hasPermission('edit_treatment_plans'));
    const canExport = useAuthStore((s) => s.hasPermission('export_treatment_plans'));

    const isNew = !id;
    const clientIdParam = searchParams.get('client') || '';
    const { data: plan, isLoading: planLoading, isError: planIsError, error: planErr } = useTxPlan(id);

    const clientIdForPage = isNew ? clientIdParam : plan?.clientId || '';

    if (isNew && !clientIdParam) {
        return (
            <div className="p-6 max-w-[900px] mx-auto">
                <BackLink />
                <Alert variant="danger">Missing client. Go back and pick one first.</Alert>
            </div>
        );
    }

    if (!isNew && planLoading) {
        return (
            <div className="p-6 max-w-[900px] mx-auto">
                <div className="p-12 flex items-center justify-center text-text-subtle">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                </div>
            </div>
        );
    }

    if (!isNew && (planIsError || !plan)) {
        return (
            <div className="p-6 max-w-[900px] mx-auto">
                <BackLink />
                <div className="p-12 text-center text-error">
                    <p className="font-medium mb-1">Couldn't load plan</p>
                    <p className="text-xs text-text-subtle">
                        {extractErrorMessage(planErr, 'Please try again.')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <PlanEditor
            clientId={clientIdForPage}
            plan={plan ?? null}
            canEdit={canEdit}
            canExport={canExport}
            onCreated={(newId) => navigate(`/treatment-plans/plans/${newId}`, { replace: true })}
        />
    );
}

function BackLink() {
    return (
        <Link to="/treatment-plans" className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to treatment plans
        </Link>
    );
}

function PlanEditor({
    clientId,
    plan,
    canEdit,
    canExport,
    onCreated,
}: {
    clientId: string;
    plan: TxPlan | null;
    canEdit: boolean;
    canExport: boolean;
    onCreated: (id: string) => void;
}) {
    const isNew = !plan;
    const status: TxPlanStatus = plan?.status ?? 'Draft';
    const isDraft = status === 'Draft';
    const isArchived = status === 'Archived';
    const isSentToPortal = !!plan?.sentToPortalAt;
    const editable = canEdit && isDraft && !isSentToPortal;

    const { data: client } = useClient(clientId);
    const { data: behaviors } = useClientBehaviors({ clientId, includeResolved: true });
    const { data: programs } = useClientPrograms({ clientId, includeRetired: true });
    const { data: bcbas } = useUserDirectory('BCBA');
    const { data: cfDefs } = useTxPlanCustomFieldDefs();

    // Default visibility in the builder is limited to the statuses that are
    // typically attached to a plan (Active for behaviors; Active/Planned for
    // programs). The "Show all …" toggles let authors opt non-active rows into
    // the plan — e.g. a Mastered program that should appear in a "Mastered
    // Goals" section of the export. Entries already stored on the plan always
    // show regardless of status so they can be unchecked.
    const [showAllBehaviors, setShowAllBehaviors] = useState(false);
    const [showAllPrograms, setShowAllPrograms] = useState(false);

    const visibleBehaviors = useMemo(() => {
        const src = behaviors ?? [];
        if (showAllBehaviors) return src;
        const existingIds = new Set((plan?.behaviors ?? []).map((b) => b.id));
        return src.filter((b) => b.status === 'Active' || existingIds.has(b.id));
    }, [behaviors, plan, showAllBehaviors]);

    const selectablePrograms = useMemo(() => {
        const src = programs ?? [];
        if (showAllPrograms) return src;
        const existingIds = new Set((plan?.programs ?? []).map((p) => p.id));
        return src.filter(
            (p) => p.status === 'Active' || p.status === 'Planned' || existingIds.has(p.id),
        );
    }, [programs, plan, showAllPrograms]);

    const [title, setTitle] = useState(plan?.title ?? '');
    const [startDate, setStartDate] = useState(plan?.startDate ?? '');
    const [reviewDate, setReviewDate] = useState(plan?.reviewDate ?? '');
    const [bcbaId, setBcbaId] = useState(plan?.bcbaId ?? '');
    const [notes, setNotes] = useState(plan?.notes ?? '');

    const initialMembership = useMemo(() => {
        if (plan) {
            return {
                behaviorIds: plan.behaviors.map((b) => b.id),
                programIds:  plan.programs.map((p) => p.id),
            };
        }
        return defaultPlanMembership(behaviors ?? [], programs ?? []);
    }, [plan, behaviors, programs]);

    const [behaviorIds, setBehaviorIds] = useState<Set<string>>(new Set(initialMembership.behaviorIds));
    const [programIds,  setProgramIds]  = useState<Set<string>>(new Set(initialMembership.programIds));
    const [membershipSeeded, setMembershipSeeded] = useState(false);

    // Plan-level custom fields. Form state is per-def keyed by def.id and
    // reinitialized whenever the catalog loads or the plan id changes. The
    // parent owns the state; CustomFieldsForm is a controlled component
    // shared with the Client edit page.
    const [cfState, setCfState] = useState<CustomFieldFormState>({});
    const [cfErrors, setCfErrors] = useState<Record<string, string>>({});
    const [cfSeeded, setCfSeeded] = useState(false);
    useEffect(() => {
        if (cfSeeded) return;
        if (!cfDefs) return;
        setCfState(initCustomFieldsForm(cfDefs, plan?.customFields ?? {}));
        setCfSeeded(true);
    }, [cfDefs, plan, cfSeeded]);

    // Seed membership from the default once behaviors+programs have loaded.
    // Skipped when editing an existing plan — we already trust plan.behaviors/programs.
    useEffect(() => {
        if (plan || membershipSeeded) return;
        if (!behaviors || !programs) return;
        const seeded = defaultPlanMembership(behaviors, programs);
        setBehaviorIds(new Set(seeded.behaviorIds));
        setProgramIds(new Set(seeded.programIds));
        setMembershipSeeded(true);
    }, [plan, behaviors, programs, membershipSeeded]);

    const [err, setErr] = useState<string | null>(null);

    const createM = useCreateTxPlan(clientId);
    const updateM = useUpdateTxPlan(clientId);
    const deleteM = useDeleteTxPlan(clientId);
    const transitionM = useTransitionTxPlan(clientId);

    function toggle(set: Set<string>, id: string): Set<string> {
        const next = new Set(set);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    }

    async function handleSave() {
        setErr(null);
        setCfErrors({});
        let cfValues = {};
        if (cfDefs && cfDefs.length) {
            const { errors, values } = validateCustomFieldsForm(cfDefs, cfState);
            if (Object.keys(errors).length) {
                setCfErrors(errors);
                setErr('Fix the highlighted custom fields below.');
                return;
            }
            cfValues = values;
        }
        const payload = {
            title: title.trim(),
            startDate: startDate || null,
            reviewDate: reviewDate || null,
            bcbaId: bcbaId || null,
            notes: notes || null,
            behaviorIds: [...behaviorIds],
            programIds:  [...programIds],
            customFields: cfValues,
        };
        if (!payload.title) {
            setErr('Title is required.');
            return;
        }
        try {
            if (plan) {
                await updateM.mutateAsync({ id: plan.id, payload });
            } else {
                const created = await createM.mutateAsync(payload);
                onCreated(created.id);
            }
        } catch (e) {
            const ax = e as AxiosError<{ error?: string }>;
            setErr(ax?.response?.data?.error || (extractErrorMessage(e, 'Save failed.')));
        }
    }

    const [signingBcba, setSigningBcba] = useState(false);
    const [signingParent, setSigningParent] = useState(false);
    const [confirmScratch, setConfirmScratch] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmTransition, setConfirmTransition] = useState<TxPlanStatus | null>(null);
    const [exporting, setExporting] = useState(false);

    return (
        <div className="p-6 max-w-[1000px] mx-auto">
            <BackLink />

            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <h1 className="font-display text-2xl font-bold text-text">
                            {isNew ? 'New treatment plan' : plan!.title}
                        </h1>
                        {!isNew && <StatusBadge status={status} />}
                    </div>
                    <p className="text-xs text-text-subtle">
                        {client ? `${client.first} ${client.last}` : 'Loading client…'}
                        {plan && <> · Created {formatDateTime(plan.createdAt)}</>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {!isNew && canExport && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExporting(true)}
                        >
                            <Download className="w-3.5 h-3.5" />
                            Export
                        </Button>
                    )}
                    {editable && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleSave}
                            disabled={createM.isPending || updateM.isPending}
                        >
                            <Save className="w-3.5 h-3.5" />
                            {createM.isPending || updateM.isPending ? 'Saving…' : isNew ? 'Create plan' : 'Save'}
                        </Button>
                    )}
                </div>
            </div>

            {err && <Alert variant="danger" className="mb-4">{err}</Alert>}

            {isSentToPortal && isDraft && (
                <Alert variant="info" className="mb-4 text-xs">
                    This plan is locked while it is out for parent signature. Recall it from the portal below to make changes.
                </Alert>
            )}

            <Card className="p-5 mb-5">
                <h2 className="font-display text-sm font-bold text-text mb-3">Plan details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    <FormField label="Title" required>
                        {(id) => (
                            <Input
                                id={id}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={!editable}
                                placeholder="e.g. 2026 Annual Treatment Plan"
                            />
                        )}
                    </FormField>
                    <FormField label="BCBA">
                        {(id) => (
                            <select
                                id={id}
                                value={bcbaId}
                                onChange={(e) => setBcbaId(e.target.value)}
                                disabled={!editable}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none disabled:opacity-50"
                            >
                                <option value="">— unassigned —</option>
                                {(bcbas ?? []).map((u) => (
                                    <option key={u.id} value={u.id}>{u.last}, {u.first}</option>
                                ))}
                            </select>
                        )}
                    </FormField>
                    <FormField label="Start date">
                        {(id) => (
                            <Input
                                id={id}
                                type="date"
                                min="1900-01-01"
                                max="9999-12-31"
                                value={startDate ?? ''}
                                onChange={(e) => setStartDate(e.target.value)}
                                disabled={!editable}
                            />
                        )}
                    </FormField>
                    <FormField label="Review date">
                        {(id) => (
                            <Input
                                id={id}
                                type="date"
                                min="1900-01-01"
                                max="9999-12-31"
                                value={reviewDate ?? ''}
                                onChange={(e) => setReviewDate(e.target.value)}
                                disabled={!editable}
                            />
                        )}
                    </FormField>
                </div>
                <FormField label="Notes">
                    {(id) => (
                        <Textarea
                            id={id}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={!editable}
                            rows={4}
                            placeholder="Summary, presenting concerns, rationale…"
                        />
                    )}
                </FormField>
            </Card>

            <Card className="p-5 mb-5">
                <div className="flex items-start justify-between gap-3 mb-1">
                    <h2 className="font-display text-sm font-bold text-text">Behaviors</h2>
                    <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showAllBehaviors}
                            onChange={(e) => setShowAllBehaviors(e.target.checked)}
                        />
                        Show resolved
                    </label>
                </div>
                <p className="text-xs text-text-subtle mb-3">
                    Active behaviors are pre-selected. Flip <b>Show resolved</b> to attach
                    a Resolved behavior — useful when the export should reference a
                    previously-addressed behavior. Editing a definition updates the live
                    record — this plan will reflect the change automatically.
                </p>
                {visibleBehaviors.length === 0 && (
                    <p className="text-sm text-text-subtle">
                        {showAllBehaviors ? 'No behaviors for this client.' : 'No active behaviors for this client.'}
                    </p>
                )}
                <ul className="space-y-1.5">
                    {visibleBehaviors.map((b) => (
                        <li key={b.id} className="flex items-start gap-2.5 text-sm">
                            <input
                                type="checkbox"
                                id={`b-${b.id}`}
                                checked={behaviorIds.has(b.id)}
                                disabled={!editable}
                                onChange={() => setBehaviorIds((s) => toggle(s, b.id))}
                                className="mt-0.5"
                            />
                            <label htmlFor={`b-${b.id}`} className="flex-1 cursor-pointer">
                                <span className="font-medium text-text">{b.name}</span>
                                {b.status !== 'Active' && <> <StatusBadge status={b.status} /></>}
                                {b.behaviorType && <span className="text-text-subtle"> · {b.behaviorType}</span>}
                                {b.definition && (
                                    <div className="text-xs text-text-subtle mt-0.5 line-clamp-2">{b.definition}</div>
                                )}
                            </label>
                        </li>
                    ))}
                </ul>
            </Card>

            <Card className="p-5 mb-5">
                <div className="flex items-start justify-between gap-3 mb-1">
                    <h2 className="font-display text-sm font-bold text-text">Programs</h2>
                    <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showAllPrograms}
                            onChange={(e) => setShowAllPrograms(e.target.checked)}
                        />
                        Show mastered &amp; retired
                    </label>
                </div>
                <p className="text-xs text-text-subtle mb-3">
                    Active and Planned programs are pre-selected. Flip <b>Show mastered &amp; retired</b>
                    to attach a Mastered or Discontinued program so it appears in the
                    exported document — e.g. a Mastered Goals section. Planned programs
                    are included in the plan but not yet running; activate them from the
                    client's Programs tab when the plan goes live.
                </p>
                {selectablePrograms.length === 0 && (
                    <p className="text-sm text-text-subtle">No programs available for this client.</p>
                )}
                <ul className="space-y-2">
                    {selectablePrograms.map((p) => (
                        <ProgramRow
                            key={p.id}
                            programId={p.id}
                            name={p.name}
                            programDomain={p.programDomain}
                            teachingMethod={p.teachingMethod}
                            status={p.status}
                            checked={programIds.has(p.id)}
                            onToggle={() => setProgramIds((s) => toggle(s, p.id))}
                            disabled={!editable}
                        />
                    ))}
                </ul>
            </Card>

            {cfDefs && cfDefs.length > 0 && (
                <div className="mb-5">
                    <CustomFieldsForm
                        defs={cfDefs}
                        value={cfState}
                        onChange={setCfState}
                        errors={cfErrors}
                        disabled={!editable}
                    />
                </div>
            )}

            {editable && (
                <div className="flex justify-end mb-5">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={createM.isPending || updateM.isPending}
                    >
                        <Save className="w-3.5 h-3.5" />
                        {createM.isPending || updateM.isPending ? 'Saving…' : isNew ? 'Create plan' : 'Save'}
                    </Button>
                </div>
            )}

            {!isNew && (
                <SignaturesSection
                    plan={plan!}
                    clientId={clientId}
                    canEdit={canEdit}
                    onSignBcba={() => setSigningBcba(true)}
                    onSignParent={() => setSigningParent(true)}
                />
            )}

            {!isNew && (
                <TransitionsBar
                    status={status}
                    canEdit={canEdit}
                    onTransition={(to) => setConfirmTransition(to)}
                    onScratch={() => setConfirmScratch(true)}
                    onDelete={() => setConfirmDelete(true)}
                    busy={transitionM.isPending || deleteM.isPending}
                />
            )}

            {signingBcba && plan && (
                <SignBcbaDialog
                    planId={plan.id}
                    clientId={clientId}
                    onClose={() => setSigningBcba(false)}
                />
            )}
            {signingParent && plan && (
                <SignParentDialog
                    planId={plan.id}
                    clientId={clientId}
                    onClose={() => setSigningParent(false)}
                />
            )}
            {confirmScratch && plan && (
                <ConfirmDialog
                    title="Scratch signatures?"
                    body="This sends the plan back to Draft and removes both signatures. The signature PNGs are purged from the database. Continue?"
                    destructive
                    onClose={() => setConfirmScratch(false)}
                    onConfirm={async () => {
                        await transitionM.mutateAsync({ id: plan.id, status: 'Draft' });
                        setConfirmScratch(false);
                    }}
                />
            )}
            {confirmDelete && plan && (
                <ConfirmDialog
                    title="Delete draft plan?"
                    body="Draft plans can be deleted permanently. Signed, Active, or Archived plans must be archived instead."
                    destructive
                    disabled={!isDraft}
                    onClose={() => setConfirmDelete(false)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(plan.id);
                        setConfirmDelete(false);
                        window.history.back();
                    }}
                />
            )}
            {confirmTransition && plan && (
                <ConfirmDialog
                    title={
                        confirmTransition === 'Active'  ? 'Activate this plan?' :
                        confirmTransition === 'Archived' ? 'Archive this plan?' :
                        `Transition → ${confirmTransition}`
                    }
                    body={
                        confirmTransition === 'Active'
                            ? 'Activating the plan marks it as the currently-enforced plan of service. Any Planned programs in this plan will remain Planned — activate them separately from the client Programs tab.'
                            : confirmTransition === 'Archived'
                                ? 'Archived plans are read-only and cannot be transitioned back. Archive this plan?'
                                : 'Proceed with transition?'
                    }
                    destructive={confirmTransition === 'Archived'}
                    onClose={() => setConfirmTransition(null)}
                    onConfirm={async () => {
                        await transitionM.mutateAsync({ id: plan.id, status: confirmTransition });
                        setConfirmTransition(null);
                    }}
                />
            )}

            {exporting && plan && (
                <ExportPlanDialog
                    planId={plan.id}
                    onClose={() => setExporting(false)}
                />
            )}

            {isArchived && (
                <Alert variant="info" className="mt-5 text-xs">
                    This plan is archived and cannot be modified. Create a new plan instead.
                </Alert>
            )}
        </div>
    );
}

function ExportPlanDialog({
    planId,
    onClose,
}: {
    planId: string;
    onClose: () => void;
}) {
    const { data: templates, isLoading, isError } = useTemplatesForExport(true);
    const [templateId, setTemplateId] = useState('');
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // Preselect the first template once the list loads.
    useEffect(() => {
        if (!templateId && templates && templates.length > 0) {
            setTemplateId(templates[0].id);
        }
    }, [templates, templateId]);

    async function handleExport() {
        if (!templateId) { setErr('Pick a template first.'); return; }
        setErr(null);
        setWorking(true);
        try {
            const { blob, filename } = await exportTxPlanDocx(planId, templateId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            onClose();
        } catch (e) {
            setErr(extractErrorMessage(e, 'Export failed.'));
        } finally {
            setWorking(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && !working && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Export treatment plan</DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="py-8 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading templates…
                    </div>
                ) : isError ? (
                    <Alert variant="danger">Couldn't load templates.</Alert>
                ) : !templates || templates.length === 0 ? (
                    <Alert variant="warning">
                        No templates available. Upload a .docx template under
                        Configuration → Treatment Plan Templates before exporting.
                    </Alert>
                ) : (
                    <>
                        <FormField label="Template" required>
                            {(id) => (
                                <select
                                    id={id}
                                    value={templateId}
                                    onChange={(e) => setTemplateId(e.target.value)}
                                    disabled={working}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none disabled:opacity-50"
                                >
                                    {templates.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                        <p className="text-xs text-text-subtle">
                            The plan will be rendered into the selected template and
                            downloaded as a .docx file. Any placeholders in the template
                            that don't match the current plan data are flagged inline.
                        </p>
                    </>
                )}

                {err && <Alert variant="danger" className="text-xs">{err}</Alert>}

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button" disabled={working}>Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="primary"
                        size="sm"
                        type="button"
                        onClick={handleExport}
                        disabled={working || !templateId || !templates || templates.length === 0}
                    >
                        <Download className="w-3.5 h-3.5" />
                        {working ? 'Exporting…' : 'Export'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ProgramRow({
    programId,
    name,
    programDomain,
    teachingMethod,
    status,
    checked,
    onToggle,
    disabled,
}: {
    programId: string;
    name: string;
    programDomain: string;
    teachingMethod: string;
    status: string;
    checked: boolean;
    onToggle: () => void;
    disabled: boolean;
}) {
    const { data: targets } = useClientProgramTargets({ clientProgramId: programId });
    const activeTargets = (targets ?? []).filter((t) => t.status === 'In Progress');
    return (
        <li className="border border-border rounded-md p-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={onToggle}
                    className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-text">{name}</span>
                        <StatusBadge status={status} />
                        {programDomain && <span className="text-xs text-text-subtle">{programDomain}</span>}
                        {teachingMethod && <span className="text-xs text-text-subtle">· {teachingMethod}</span>}
                    </div>
                    {activeTargets.length > 0 && (
                        <ul className="mt-2 ml-4 space-y-0.5">
                            {activeTargets.map((t) => (
                                <li key={t.id} className="text-xs text-text-muted">
                                    • {t.name} <span className="text-text-subtle">({t.dataType})</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </label>
        </li>
    );
}

function SignaturesSection({
    plan,
    clientId,
    canEdit,
    onSignBcba,
    onSignParent,
}: {
    plan: TxPlan;
    clientId: string;
    canEdit: boolean;
    onSignBcba: () => void;
    onSignParent: () => void;
}) {
    const isDraft = plan.status === 'Draft';
    const bcbaSigned = !!plan.signedAtBcba;
    const parentSigned = !!plan.signedAtParent;
    const sentAt = plan.sentToPortalAt;
    const isSent = !!sentAt;
    const sendM = useSendPlanToPortal(clientId);
    const recallM = useRecallPlanFromPortal(clientId);

    return (
        <Card className="p-5 mb-5">
            <h2 className="font-display text-sm font-bold text-text mb-3">Signatures</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SignatureBlock
                    label="BCBA"
                    signed={bcbaSigned}
                    signerName={plan.signedNameBcba}
                    signedAt={plan.signedAtBcba}
                    imageKey={bcbaSigned ? { planId: plan.id, kind: 'bcba', updatedAt: plan.updatedAt } : null}
                    canSign={canEdit && isDraft && !bcbaSigned && !isSent}
                    canReSign={canEdit && isDraft && bcbaSigned && !isSent}
                    onSign={onSignBcba}
                />
                <SignatureBlock
                    label="Parent / Guardian"
                    signed={parentSigned}
                    signerName={plan.signedNameParent}
                    signedAt={plan.signedAtParent}
                    relationship={plan.signedRelationshipParent}
                    imageKey={parentSigned ? { planId: plan.id, kind: 'parent', updatedAt: plan.updatedAt } : null}
                    canSign={canEdit && isDraft && bcbaSigned && !parentSigned}
                    canReSign={false}
                    onSign={onSignParent}
                    disabledHint={!bcbaSigned ? 'BCBA must sign first.' : null}
                />
            </div>
            {canEdit && isDraft && bcbaSigned && !parentSigned && (
                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-3">
                    <div className="text-xs text-text-subtle">
                        {sentAt
                            ? <>Sent to the parent portal for signature on {formatDateTime(sentAt)}.</>
                            : <>The family can sign from the parent portal once you send this plan.</>}
                    </div>
                    {sentAt
                        ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => recallM.mutate(plan.id)}
                                disabled={recallM.isPending}
                            >
                                Recall from portal
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => sendM.mutate(plan.id)}
                                disabled={sendM.isPending}
                            >
                                Send to portal
                            </Button>
                        )}
                </div>
            )}
        </Card>
    );
}

function SignatureBlock({
    label,
    signed,
    signerName,
    signedAt,
    relationship,
    imageKey,
    canSign,
    canReSign,
    onSign,
    disabledHint,
}: {
    label: string;
    signed: boolean;
    signerName: string;
    signedAt: string | null;
    relationship?: string;
    imageKey: { planId: string; kind: 'bcba' | 'parent'; updatedAt: string } | null;
    canSign: boolean;
    canReSign: boolean;
    onSign: () => void;
    disabledHint?: string | null;
}) {
    return (
        <div className="border border-border rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-text-muted uppercase tracking-wider">{label}</p>
                {signed
                    ? <StatusBadge status="Signed" />
                    : <StatusBadge status="Not Started" />}
            </div>
            {signed ? (
                <>
                    {imageKey && (
                        <AuthenticatedSignatureImage
                            planId={imageKey.planId}
                            kind={imageKey.kind}
                            refreshKey={imageKey.updatedAt}
                            alt={`${label} signature`}
                        />
                    )}
                    <p className="text-sm text-text mt-2">{signerName}</p>
                    {relationship && <p className="text-xs text-text-subtle">{relationship}</p>}
                    <p className="text-xs text-text-subtle">{formatDateTime(signedAt)}</p>
                    {canReSign && (
                        <Button variant="ghost" size="xs" onClick={onSign} className="mt-2">
                            <RotateCcw className="w-3 h-3" /> Re-sign
                        </Button>
                    )}
                </>
            ) : (
                <>
                    <div className="h-24 border border-dashed border-border rounded bg-surface-muted flex items-center justify-center text-xs text-text-subtle">
                        Not yet signed
                    </div>
                    {canSign ? (
                        <Button variant="primary" size="sm" onClick={onSign} className="mt-2 w-full">
                            Capture signature
                        </Button>
                    ) : disabledHint ? (
                        <p className="text-xs text-text-subtle mt-2">{disabledHint}</p>
                    ) : null}
                </>
            )}
        </div>
    );
}

/*
 * Signature PNGs sit behind the JWT-auth'd `/tx-plans/:id/signatures/:kind/image`
 * endpoint. An `<img src="…">` tag can't attach an Authorization header, so we
 * fetch the blob through axios, create an object URL, and render that. The
 * object URL lives only for this component instance and is revoked on unmount.
 */
function AuthenticatedSignatureImage({
    planId,
    kind,
    refreshKey,
    alt,
}: {
    planId: string;
    kind: 'bcba' | 'parent';
    refreshKey: string;
    alt: string;
}) {
    const [src, setSrc] = useState<string | null>(null);
    useEffect(() => {
        let cancelled = false;
        let currentUrl: string | null = null;
        (async () => {
            try {
                const res = await api.get<Blob>(`/tx-plans/${planId}/signatures/${kind}/image`, {
                    responseType: 'blob',
                });
                if (cancelled) return;
                currentUrl = URL.createObjectURL(res.data);
                setSrc(currentUrl);
            } catch {
                // fall through — the loading placeholder stays
            }
        })();
        return () => {
            cancelled = true;
            if (currentUrl) URL.revokeObjectURL(currentUrl);
        };
    }, [planId, kind, refreshKey]);

    if (!src) {
        return (
            <div className="h-24 bg-surface border border-border rounded flex items-center justify-center text-text-subtle">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={alt}
            className="w-full h-24 object-contain bg-surface border border-border rounded"
        />
    );
}

function TransitionsBar({
    status,
    canEdit,
    onTransition,
    onScratch,
    onDelete,
    busy,
}: {
    status: TxPlanStatus;
    canEdit: boolean;
    onTransition: (to: TxPlanStatus) => void;
    onScratch: () => void;
    onDelete: () => void;
    busy: boolean;
}) {
    if (!canEdit) return null;
    return (
        <Card className="p-4 mb-5 flex flex-wrap items-center gap-2">
            {status === 'Draft' && (
                <>
                    <Button variant="ghost" size="sm" onClick={() => onTransition('Archived')} disabled={busy}>
                        Archive
                    </Button>
                    <Button variant="danger" size="sm" onClick={onDelete} disabled={busy}>
                        Delete draft
                    </Button>
                </>
            )}
            {status === 'Signed' && (
                <>
                    <Button variant="primary" size="sm" onClick={() => onTransition('Active')} disabled={busy}>
                        Activate
                    </Button>
                    <Button variant="ghost" size="sm" onClick={onScratch} disabled={busy}>
                        <RotateCcw className="w-3.5 h-3.5" /> Scratch signatures
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onTransition('Archived')} disabled={busy}>
                        Archive
                    </Button>
                </>
            )}
            {status === 'Active' && (
                <Button variant="ghost" size="sm" onClick={() => onTransition('Archived')} disabled={busy}>
                    Archive
                </Button>
            )}
        </Card>
    );
}

function SignBcbaDialog({
    planId,
    clientId,
    onClose,
}: {
    planId: string;
    clientId: string;
    onClose: () => void;
}) {
    const signM = useSignBcba(clientId);
    const [name, setName] = useState('');
    const [err, setErr] = useState<string | null>(null);
    const canvasRef = useRef<SignatureCanvasHandle | null>(null);
    const [working, setWorking] = useState(false);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required.'); return; }
        const dataUrl = canvasRef.current?.toDataUrl();
        if (!dataUrl) { setErr('Please draw your signature.'); return; }
        setWorking(true);
        try {
            await signM.mutateAsync({ id: planId, payload: { signerName: name.trim(), signatureImage: dataUrl } });
            onClose();
        } catch (e) {
            const ax = e as AxiosError<{ error?: string }>;
            setErr(ax?.response?.data?.error || (extractErrorMessage(e, 'Could not save signature.')));
        } finally {
            setWorking(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>BCBA signature</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <FormField label="Printed name" required>
                        {(id) => (
                            <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />
                        )}
                    </FormField>
                    <div>
                        <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">Signature</p>
                        <SignatureCanvas ref={canvasRef} />
                        <div className="mt-1.5 flex justify-end">
                            <Button variant="ghost" size="xs" type="button" onClick={() => canvasRef.current?.clear()}>
                                Clear
                            </Button>
                        </div>
                    </div>
                    {err && <p className="text-xs text-error">{err}</p>}
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={working}>
                            {working ? 'Saving…' : 'Save signature'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function SignParentDialog({
    planId,
    clientId,
    onClose,
}: {
    planId: string;
    clientId: string;
    onClose: () => void;
}) {
    const signM = useSignParent(clientId);
    const { data: relationshipOptions = [] } = useActiveParentRelationships();
    const [name, setName] = useState('');
    const [relationship, setRelationship] = useState('');
    const [err, setErr] = useState<string | null>(null);
    const canvasRef = useRef<SignatureCanvasHandle | null>(null);
    const [working, setWorking] = useState(false);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required.'); return; }
        if (!relationship.trim()) { setErr('Relationship is required.'); return; }
        const dataUrl = canvasRef.current?.toDataUrl();
        if (!dataUrl) { setErr('Please draw your signature.'); return; }
        setWorking(true);
        try {
            await signM.mutateAsync({
                id: planId,
                payload: { signerName: name.trim(), relationship: relationship.trim(), signatureImage: dataUrl },
            });
            onClose();
        } catch (e) {
            const ax = e as AxiosError<{ error?: string }>;
            setErr(ax?.response?.data?.error || (extractErrorMessage(e, 'Could not save signature.')));
        } finally {
            setWorking(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Parent / Guardian signature</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Printed name" required>
                            {(id) => (
                                <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />
                            )}
                        </FormField>
                        <FormField label="Relationship" required>
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={relationship}
                                    onChange={(e) => setRelationship(e.target.value)}
                                >
                                    <option value="">Select…</option>
                                    {relationshipOptions.map((r) => (
                                        <option key={r.id} value={r.label}>{r.label}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                    </div>
                    <div>
                        <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">Signature</p>
                        <SignatureCanvas ref={canvasRef} />
                        <div className="mt-1.5 flex justify-end">
                            <Button variant="ghost" size="xs" type="button" onClick={() => canvasRef.current?.clear()}>
                                Clear
                            </Button>
                        </div>
                    </div>
                    {err && <p className="text-xs text-error">{err}</p>}
                    <Alert variant="info" className="text-xs">
                        Signing here also moves the plan to <b>Signed</b> status.
                    </Alert>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={working}>
                            {working ? 'Saving…' : 'Save signature'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ConfirmDialog({
    title,
    body,
    destructive,
    disabled,
    onClose,
    onConfirm,
}: {
    title: string;
    body: string;
    destructive?: boolean;
    disabled?: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">{body}</p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant={destructive ? 'danger' : 'primary'}
                        size="sm"
                        disabled={working || disabled}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try {
                                await onConfirm();
                            } catch (e) {
                                const ax = e as AxiosError<{ error?: string }>;
                                setErr(ax?.response?.data?.error || (extractErrorMessage(e, 'Could not proceed.')));
                            } finally {
                                setWorking(false);
                            }
                        }}
                    >
                        {working ? 'Working…' : 'Confirm'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
